export const ONBOARDING_GENERATION_TIMEOUT_MS = 90_000;
export const ONBOARDING_PERSONA_GENERATION_SLOW_WARNING_MS = 90_000;

type BrandProfileGenerationStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "PARTIAL" | "FAILED";

type AutoStartPersonaGenerationInput = {
  brandId: string | null | undefined;
  hasAutoTriggeredPersonaGeneration: boolean;
  onboardingPersonaId: string | null;
  existingPersonaCount: number | null;
  isLoadingPersonas: boolean;
  isBroadcastSubscribed: boolean;
  isGenerating: boolean;
};

export const shouldAutoStartPersonaGeneration = ({
  brandId,
  hasAutoTriggeredPersonaGeneration,
  onboardingPersonaId,
  existingPersonaCount,
  isLoadingPersonas,
  isBroadcastSubscribed,
  isGenerating,
}: AutoStartPersonaGenerationInput): boolean => {
  if (!brandId || isLoadingPersonas || hasAutoTriggeredPersonaGeneration || onboardingPersonaId || isGenerating) {
    return false;
  }

  if (!isBroadcastSubscribed || existingPersonaCount === null) {
    return false;
  }

  return existingPersonaCount === 0;
};

type AutoStartInitialPromptGenerationInput = {
  searchSpecificationId: string | undefined;
  hasTriggeredInitialGeneration: boolean;
  hasFetchedExistingPrompts: boolean;
  isLoadingExistingPrompts: boolean;
  existingPromptCount: number;
  isBroadcastSubscribed: boolean;
  isRequestPending: boolean;
  hasActiveGeneration: boolean;
};

export const shouldAutoStartInitialPromptGeneration = ({
  searchSpecificationId,
  hasTriggeredInitialGeneration,
  hasFetchedExistingPrompts,
  isLoadingExistingPrompts,
  existingPromptCount,
  isBroadcastSubscribed,
  isRequestPending,
  hasActiveGeneration,
}: AutoStartInitialPromptGenerationInput): boolean => {
  if (
    !searchSpecificationId ||
    hasTriggeredInitialGeneration ||
    !hasFetchedExistingPrompts ||
    isLoadingExistingPrompts ||
    existingPromptCount > 0 ||
    !isBroadcastSubscribed ||
    isRequestPending ||
    hasActiveGeneration
  ) {
    return false;
  }

  return true;
};

type InitialPromptGenerationViewStateInput = {
  searchSpecificationId: string | undefined;
  hasFetchedExistingPrompts: boolean;
  isLoadingExistingPrompts: boolean;
  hasTriggeredInitialGeneration: boolean;
  initialPromptGenerationId: string | null;
  promptDraftCount: number;
  initialPromptGenerationError: string | null;
  hasSubscriptionError: boolean;
};

export const getInitialPromptGenerationViewState = ({
  searchSpecificationId,
  hasFetchedExistingPrompts,
  isLoadingExistingPrompts,
  hasTriggeredInitialGeneration,
  initialPromptGenerationId,
  promptDraftCount,
  initialPromptGenerationError,
  hasSubscriptionError,
}: InitialPromptGenerationViewStateInput): "loading" | "ready" | "empty" | "error" => {
  if (promptDraftCount > 0) {
    return "ready";
  }

  if (
    !!searchSpecificationId &&
    (!hasFetchedExistingPrompts || isLoadingExistingPrompts || initialPromptGenerationId !== null)
  ) {
    return "loading";
  }

  if (initialPromptGenerationError || hasSubscriptionError) {
    return "error";
  }

  if (hasTriggeredInitialGeneration) {
    return "empty";
  }

  return "loading";
};

type InitialBrandProfileGenerationViewStateInput = {
  hasBrand: boolean;
  hasGeneratedBrandProfile: boolean;
  latestInitialGenerationStatus: BrandProfileGenerationStatus | null | undefined;
};

export const getInitialBrandProfileGenerationViewState = ({
  hasBrand,
  hasGeneratedBrandProfile,
  latestInitialGenerationStatus,
}: InitialBrandProfileGenerationViewStateInput): "loading" | "ready" => {
  if (!hasBrand) {
    return "ready";
  }

  if (latestInitialGenerationStatus) {
    return latestInitialGenerationStatus === "SUCCEEDED" ||
      latestInitialGenerationStatus === "PARTIAL" ||
      latestInitialGenerationStatus === "FAILED"
      ? "ready"
      : "loading";
  }

  return hasGeneratedBrandProfile ? "ready" : "loading";
};
